import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Printer, FileText, Save, Cloud, HardDrive, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { projectsApi, settingsApi, getDownloadAuthParam } from "@/lib/api";
import { saveBrowserEstimate } from "@/lib/browserStore";
import { formatCurrency, formatDate } from "@/lib/utils";

export default function Summary() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectRes, estimateRes, settingsRes] = await Promise.all([
          projectsApi.getById(projectId), projectsApi.getEstimate(projectId), settingsApi.get()
        ]);
        setProject(projectRes.data);
        setEstimate(estimateRes.data);
        setSettings(settingsRes.data);
      } catch (error) { toast.error("Failed to load"); }
      finally { setLoading(false); }
    };
    loadData();
  }, [projectId]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/pdf?${getDownloadAuthParam()}`,
        { method: 'GET' }
      );
      
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name.replace(/\s+/g, '_')}_Estimate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToBrowser = () => {
    try {
      saveBrowserEstimate({ project, estimate });
      toast.success('Saved to this browser');
    } catch (error) {
      toast.error('Could not save to browser');
    }
  };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!project || !estimate) return null;

  return (
    <div className="page-container">
      <header className="app-header no-print">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}`)}><ArrowLeft className="w-5 h-5" /></Button>
            <div><h1 className="font-heading text-xl font-bold">Project Summary</h1><p className="text-xs text-muted-foreground">{project.name}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="btn-primary" data-testid="save-menu-trigger">
                  <Save className="w-4 h-4 mr-2" />Save
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => toast.success('Saved to the cloud on this device')} data-testid="save-cloud">
                  <Cloud className="w-4 h-4 mr-2" />Save to Cloud
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSaveToBrowser} data-testid="save-browser">
                  <HardDrive className="w-4 h-4 mr-2" />Save to Browser
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownloadPDF} data-testid="save-pdf">
                  <Download className="w-4 h-4 mr-2" />Download as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.print()} data-testid="save-print">
                  <Printer className="w-4 h-4 mr-2" />Print
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button className="btn-primary" onClick={() => navigate(`/project/${projectId}/proposal`)} data-testid="generate-proposal">
              <FileText className="w-4 h-4 mr-2" />Generate Proposal
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">{project.name}</h2>
                <div className="space-y-2 text-sm">
                  <p><span className="text-muted-foreground">Client:</span> {project.client_name || "N/A"}</p>
                  <p><span className="text-muted-foreground">Address:</span> {project.client_address || "N/A"}</p>
                  <p><span className="text-muted-foreground">Phone:</span> {project.client_phone || "N/A"}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Estimate Date</p>
                <p className="font-mono">{formatDate(new Date())}</p>
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Labor Rate</p>
                  <p className="font-mono">{formatCurrency(project.labor_rate)}/hr</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {estimate.interior_breakdowns?.length > 0 && (
          <Card className="mb-8">
            <CardHeader><CardTitle className="font-heading text-lg">Interior Rooms</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Room</TableHead><TableHead>Dimensions</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Labor</TableHead><TableHead className="text-right">Materials</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {estimate.interior_breakdowns.map((room) => (
                    <TableRow key={room.room_id}><TableCell className="font-medium">{room.room_name}</TableCell><TableCell className="font-mono text-sm">{room.dimensions}</TableCell><TableCell className="text-right font-mono">{room.total_hours}</TableCell><TableCell className="text-right font-mono">{formatCurrency(room.labor_cost)}</TableCell><TableCell className="text-right font-mono">{formatCurrency(room.material_cost)}</TableCell><TableCell className="text-right font-mono font-semibold">{formatCurrency(room.labor_cost + room.material_cost)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {estimate.exterior_breakdowns?.length > 0 && (
          <Card className="mb-8">
            <CardHeader><CardTitle className="font-heading text-lg">Exterior Sides</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Side</TableHead><TableHead className="text-right">Sq Ft</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Labor</TableHead><TableHead className="text-right">Materials</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {estimate.exterior_breakdowns.map((side) => (
                    <TableRow key={side.side_id}><TableCell className="font-medium">{side.side_name}</TableCell><TableCell className="text-right font-mono">{side.sq_ft}</TableCell><TableCell className="text-right font-mono">{side.total_hours}</TableCell><TableCell className="text-right font-mono">{formatCurrency(side.labor_cost)}</TableCell><TableCell className="text-right font-mono">{formatCurrency(side.material_cost)}</TableCell><TableCell className="text-right font-mono font-semibold">{formatCurrency(side.labor_cost + side.material_cost)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {estimate.deck_breakdowns?.length > 0 && (
          <Card className="mb-8">
            <CardHeader><CardTitle className="font-heading text-lg">Deck Staining</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Deck</TableHead><TableHead className="text-right">Sq Ft</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Labor</TableHead><TableHead className="text-right">Materials</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {estimate.deck_breakdowns.map((deck) => (
                    <TableRow key={deck.deck_id}><TableCell className="font-medium">{deck.deck_name}</TableCell><TableCell className="text-right font-mono">{deck.sq_ft}</TableCell><TableCell className="text-right font-mono">{deck.total_hours}</TableCell><TableCell className="text-right font-mono">{formatCurrency(deck.labor_cost)}</TableCell><TableCell className="text-right font-mono">{formatCurrency(deck.material_cost)}</TableCell><TableCell className="text-right font-mono font-semibold">{formatCurrency(deck.labor_cost + deck.material_cost)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {estimate.cabinet_breakdowns?.length > 0 && (
          <Card className="mb-8">
            <CardHeader><CardTitle className="font-heading text-lg">Cabinet Refinishing</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Cabinet Area</TableHead><TableHead>Mode</TableHead><TableHead>Tier</TableHead><TableHead className="text-right">Core Price</TableHead><TableHead className="text-right">Logistics Cost</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {estimate.cabinet_breakdowns.map((cabinet) => (
                    <TableRow key={cabinet.cabinet_id}>
                      <TableCell className="font-medium">{cabinet.cabinet_name}</TableCell>
                      <TableCell className="font-mono text-sm">{cabinet.pricing_mode === "per_unit" ? "Per Unit" : "Linear Foot"}</TableCell>
                      <TableCell className="font-mono text-sm">{cabinet.tier_label}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cabinet.core_price)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cabinet.labor_cost)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(cabinet.core_price + cabinet.labor_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Card className="mb-8">
          <CardHeader><CardTitle className="font-heading text-lg">Materials Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(estimate.total_material_gallons || {}).map(([type, gallons]) => (
                <div key={type} className="p-4 bg-secondary/50 rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{type.replace('_', ' ')}</p>
                  <p className="font-mono text-xl">{gallons} <span className="text-sm text-muted-foreground">gal</span></p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Estimate Totals</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Total Labor Hours</span><span className="font-mono">{estimate.total_labor_hours} hours{estimate.nonproductive_hours > 0 ? ` (+${estimate.nonproductive_hours} nonprod.)` : ""}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Labor Cost</span><span className="font-mono">{formatCurrency(estimate.total_labor_cost)}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Material Cost</span><span className="font-mono">{formatCurrency(estimate.total_material_cost)}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Direct Cost (Subtotal)</span><span className="font-mono">{formatCurrency(estimate.subtotal)}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Overhead</span><span className="font-mono">{formatCurrency(estimate.overhead_amount)}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Profit</span><span className="font-mono">{formatCurrency(estimate.profit_amount)}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-border"><span>Incentive</span><span className="font-mono">{formatCurrency(estimate.incentive_amount)}</span></div>
              <div className="flex justify-between items-center py-4 bg-amber-500/10 px-4 -mx-4 rounded-sm">
                <span className="font-heading text-xl font-bold">Total Estimate</span>
                <span className="font-mono text-2xl text-amber-500 font-bold">{formatCurrency(estimate.client_total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {estimate.payment_schedule?.length > 0 && (
          <Card className="mt-8">
            <CardHeader><CardTitle className="font-heading text-lg">Payment Schedule</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {estimate.payment_schedule.map(p => (
                  <div key={p.label} className="flex justify-between items-center py-2 border-b border-border"><span>{p.label}</span><span className="font-mono">{formatCurrency(p.amount)}</span></div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
